import { useState } from 'react';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import { TutorBookingTabs } from '@/components/tutor-booking-tabs';
import {
  useSetTutorBookingEnabled,
  useTutorBookingStatus,
  useTutorConnectOnboard,
} from '@/lib/tutor/booking';

export function TutorBookingPaymentsPage() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const { data: status, isLoading } = useTutorBookingStatus(profile?.id, admin);
  const setEnabled = useSetTutorBookingEnabled(profile?.id);
  const onboard = useTutorConnectOnboard();
  const [error, setError] = useState<string | null>(null);

  const canEnable = admin || Boolean(status?.stripe_connect_charges_enabled);

  const handleOnboard = async () => {
    setError(null);
    try {
      const { url } = await onboard.mutateAsync(window.location.href);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de démarrer la configuration.');
    }
  };

  const handleToggle = async () => {
    if (!status) return;
    setError(null);
    try {
      await setEnabled.mutateAsync(!status.booking_enabled);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de modifier le réglage.');
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Rendez-vous
      </h1>
      <TutorBookingTabs />

      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      )}

      {status && (
        <div className="space-y-4">
          {!admin && (
            <div className="border-ink-100 rounded-2xl border bg-white p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-ink-900 text-sm font-semibold">Paiements Stripe</p>
                  <p className="text-ink-500 mt-0.5 text-xs">
                    {status.stripe_connect_charges_enabled
                      ? 'Ton compte est configuré, tu peux recevoir des paiements.'
                      : 'Configure ton compte pour être payé directement pour tes cours.'}
                  </p>
                </div>
                {status.stripe_connect_charges_enabled && (
                  <CheckCircle2 className="text-primary-500 h-6 w-6 shrink-0" />
                )}
              </div>
              <Button
                onClick={handleOnboard}
                disabled={onboard.isPending}
                className="mt-4 w-full gap-2"
              >
                {onboard.isPending
                  ? 'Redirection…'
                  : status.stripe_connect_charges_enabled
                    ? 'Gérer mon compte Stripe'
                    : 'Configurer mes paiements'}
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          )}

          {admin && (
            <div className="border-ink-100 rounded-2xl border bg-white p-5">
              <p className="text-ink-900 text-sm font-semibold">Paiements</p>
              <p className="text-ink-500 mt-0.5 text-xs">
                Compte admin : les paiements restent sur le compte plateforme JeRaisonne, pas besoin
                de configurer Stripe Connect.
              </p>
            </div>
          )}

          <div className="border-ink-100 flex items-center justify-between gap-3 rounded-2xl border bg-white p-5">
            <div className="min-w-0">
              <p className="text-ink-900 text-sm font-semibold">Proposer des cours particuliers</p>
              <p className="text-ink-500 mt-0.5 text-xs">
                Visible sur ta fiche publique et dans l'app de tes élèves.
              </p>
              {!canEnable && (
                <p className="text-accent-600 mt-1 text-xs">
                  Configure tes paiements avant d'activer.
                </p>
              )}
            </div>
            <button
              type="button"
              disabled={!canEnable || setEnabled.isPending}
              onClick={handleToggle}
              role="switch"
              aria-checked={status.booking_enabled}
              className={[
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                status.booking_enabled
                  ? 'bg-accent-400 focus:ring-accent-400'
                  : 'bg-ink-200 focus:ring-ink-400',
              ].join(' ')}
            >
              <span
                className={[
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform duration-200',
                  status.booking_enabled ? 'translate-x-5' : 'translate-x-0',
                ].join(' ')}
              />
            </button>
          </div>

          {error && <p className="text-accent-600 font-sans text-xs">{error}</p>}
        </div>
      )}
    </div>
  );
}
